import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

// Curated list — jewellery-CAD relevant markets first, then a broader long-tail
// for global buyers. Emoji flags render everywhere without extra asset bundles.
// national_len is the "typical" digits-after-country-code so the input's `maxLength`
// stays sensible while still tolerating shorter numbers (e.g., 8-digit HK/SG).
export const COUNTRIES = [
  { code: "IN", dial: "+91",  name: "India",          flag: "🇮🇳", national_len: 10 },
  { code: "US", dial: "+1",   name: "United States",  flag: "🇺🇸", national_len: 10 },
  { code: "GB", dial: "+44",  name: "United Kingdom", flag: "🇬🇧", national_len: 10 },
  { code: "AE", dial: "+971", name: "UAE",            flag: "🇦🇪", national_len: 9  },
  { code: "SA", dial: "+966", name: "Saudi Arabia",   flag: "🇸🇦", national_len: 9  },
  { code: "SG", dial: "+65",  name: "Singapore",      flag: "🇸🇬", national_len: 8  },
  { code: "HK", dial: "+852", name: "Hong Kong",      flag: "🇭🇰", national_len: 8  },
  { code: "CA", dial: "+1",   name: "Canada",         flag: "🇨🇦", national_len: 10 },
  { code: "AU", dial: "+61",  name: "Australia",      flag: "🇦🇺", national_len: 9  },
  { code: "NZ", dial: "+64",  name: "New Zealand",    flag: "🇳🇿", national_len: 9  },
  { code: "MY", dial: "+60",  name: "Malaysia",       flag: "🇲🇾", national_len: 9  },
  { code: "TH", dial: "+66",  name: "Thailand",       flag: "🇹🇭", national_len: 9  },
  { code: "ID", dial: "+62",  name: "Indonesia",      flag: "🇮🇩", national_len: 10 },
  { code: "PH", dial: "+63",  name: "Philippines",    flag: "🇵🇭", national_len: 10 },
  { code: "VN", dial: "+84",  name: "Vietnam",        flag: "🇻🇳", national_len: 9  },
  { code: "JP", dial: "+81",  name: "Japan",          flag: "🇯🇵", national_len: 10 },
  { code: "KR", dial: "+82",  name: "South Korea",    flag: "🇰🇷", national_len: 10 },
  { code: "CN", dial: "+86",  name: "China",          flag: "🇨🇳", national_len: 11 },
  { code: "DE", dial: "+49",  name: "Germany",        flag: "🇩🇪", national_len: 11 },
  { code: "FR", dial: "+33",  name: "France",         flag: "🇫🇷", national_len: 9  },
  { code: "IT", dial: "+39",  name: "Italy",          flag: "🇮🇹", national_len: 10 },
  { code: "ES", dial: "+34",  name: "Spain",          flag: "🇪🇸", national_len: 9  },
  { code: "NL", dial: "+31",  name: "Netherlands",    flag: "🇳🇱", national_len: 9  },
  { code: "BE", dial: "+32",  name: "Belgium",        flag: "🇧🇪", national_len: 9  },
  { code: "CH", dial: "+41",  name: "Switzerland",    flag: "🇨🇭", national_len: 9  },
  { code: "SE", dial: "+46",  name: "Sweden",         flag: "🇸🇪", national_len: 9  },
  { code: "TR", dial: "+90",  name: "Turkey",         flag: "🇹🇷", national_len: 10 },
  { code: "IL", dial: "+972", name: "Israel",         flag: "🇮🇱", national_len: 9  },
  { code: "ZA", dial: "+27",  name: "South Africa",   flag: "🇿🇦", national_len: 9  },
  { code: "BR", dial: "+55",  name: "Brazil",         flag: "🇧🇷", national_len: 11 },
  { code: "MX", dial: "+52",  name: "Mexico",         flag: "🇲🇽", national_len: 10 },
  { code: "AR", dial: "+54",  name: "Argentina",      flag: "🇦🇷", national_len: 10 },
  { code: "EG", dial: "+20",  name: "Egypt",          flag: "🇪🇬", national_len: 10 },
  { code: "KE", dial: "+254", name: "Kenya",          flag: "🇰🇪", national_len: 9  },
  { code: "NG", dial: "+234", name: "Nigeria",        flag: "🇳🇬", national_len: 10 },
  { code: "PK", dial: "+92",  name: "Pakistan",       flag: "🇵🇰", national_len: 10 },
  { code: "BD", dial: "+880", name: "Bangladesh",     flag: "🇧🇩", national_len: 10 },
  { code: "LK", dial: "+94",  name: "Sri Lanka",      flag: "🇱🇰", national_len: 9  },
  { code: "NP", dial: "+977", name: "Nepal",          flag: "🇳🇵", national_len: 10 },
];

const DEFAULT_COUNTRY = COUNTRIES[0]; // India — always the default, only overridden if IP-geo detects a genuinely different country.

/** Optional IP-geo fallback. Runs asynchronously after mount. If the user
 *  is browsing from a country other than India, we switch to their flag
 *  so they don't have to hunt for it. Manual selection wins over this. */
async function detectCountryFromIP() {
  try {
    const r = await fetch("https://ipapi.co/json/", { cache: "force-cache" });
    if (!r.ok) return null;
    const data = await r.json();
    const region = String(data?.country_code || "").toUpperCase();
    return COUNTRIES.find((c) => c.code === region) || null;
  } catch (_) {
    return null;
  }
}

/**
 * Composite country-code picker + national-number input.
 * `value` and `onChange` operate on the FULL number as digits WITH country
 * code (e.g., "919876543210") so downstream code doesn't need to reason about
 * the split. `onCountryChange` fires the selected country object separately
 * for callers that need it (e.g., MSG91's identifier).
 */
export default function CountryPhoneInput({ value = "", onChange, disabled, testid = "reg-phone", verified = false }) {
  // Default is ALWAYS India (+91). Only override when IP-geo detects the
  // user is browsing from somewhere else. Manual selection always wins.
  const [country, setCountry] = useState(DEFAULT_COUNTRY);
  const [national, setNational] = useState("");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const userPickedRef = useRef(false); // set true once the user manually picks a country
  const wrapperRef = useRef(null);

  // IP-geo fallback — runs once. Only overrides the India default if the
  // detected country is DIFFERENT from India and the user hasn't manually
  // picked yet. Also skips entirely when the parent has already disabled
  // the picker (e.g., OTP has been verified) — flipping the flag mid-
  // session would silently invalidate downstream verification.
  useEffect(() => {
    if (userPickedRef.current || disabled) return;
    let cancelled = false;
    detectCountryFromIP().then((c) => {
      if (!cancelled && c && c.code !== "IN" && !userPickedRef.current && !disabled) {
        setCountry(c);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync internal state when the parent forces a new value (e.g., reset).
  useEffect(() => {
    if (!value) {
      setNational("");
      return;
    }
    // Try to match a known dial prefix at the start of the digits.
    const digits = String(value).replace(/\D/g, "");
    // Longest-prefix match so +1 doesn't shadow +971.
    const match = [...COUNTRIES]
      .sort((a, b) => b.dial.length - a.dial.length)
      .find((c) => digits.startsWith(c.dial.slice(1)));
    if (match) {
      setCountry(match);
      setNational(digits.slice(match.dial.length - 1));
    } else {
      setNational(digits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Emit combined digits whenever either half changes.
  useEffect(() => {
    const combined = country.dial.replace("+", "") + national;
    onChange?.(combined ? `+${combined}` : "", country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, national]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  const handleNational = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 15); // ITU max E.164 = 15
    setNational(digits);
  };

  const selectCountry = (c) => {
    userPickedRef.current = true;
    setCountry(c);
    setOpen(false);
    setQuery("");
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative flex items-stretch bg-card border rounded-md focus-within:border-primary transition-colors ${verified ? "border-emerald-500 focus-within:border-emerald-500" : "border-border"}`}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        data-testid="country-code-picker"
        className="flex items-center gap-1.5 pl-3 pr-2 border-r border-border hover:bg-secondary/50 transition-colors rounded-l-md disabled:opacity-50"
      >
        <span className="text-lg leading-none" aria-hidden>{country.flag}</span>
        <span className="text-sm font-mono font-semibold">{country.dial}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <input
        type="tel"
        required
        inputMode="numeric"
        value={national}
        disabled={disabled}
        data-testid={testid}
        placeholder={country.national_len ? `${country.national_len}-digit number` : "Mobile number"}
        onChange={(e) => handleNational(e.target.value)}
        className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm focus:outline-none disabled:opacity-50"
      />

      {open && (
        <div
          data-testid="country-code-dropdown"
          className="absolute top-full left-0 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-md border border-border bg-popover shadow-xl z-50 overflow-hidden"
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country or code"
              data-testid="country-search-input"
              className="flex-1 bg-transparent text-sm focus:outline-none"
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-muted-foreground">No matches</li>
            )}
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => selectCountry(c)}
                  data-testid={`country-option-${c.code}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-secondary transition-colors ${c.code === country.code ? "bg-primary/5 font-semibold" : ""}`}
                >
                  <span className="text-lg leading-none" aria-hidden>{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs font-mono text-muted-foreground">{c.dial}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
